import * as React from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, CheckCircle, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { StepLoanType, StepPersonalDetails } from './onboarding-steps';
import { useFormSessionTracking, trackConverted, trackStep, readDealerCode, readEntryPage, trafficSourceFields } from '../lib/formSession';
import { DEFAULT_TERM_MONTHS, INDICATIVE_RATE, formatCurrency, weeklyRepayment } from '../lib/finance';

const API = `${process.env.REACT_APP_BACKEND_URL || '/_/backend'}/api`;

const TOTAL = 3;
const STEP_KEYS = { 1: 'hero_loan_type', 2: 'hero_contact', 3: 'hero_consent' };

/** A finite number, or null. Distinguishes a real 0 from an absent value. */
const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

const looksLikeEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
const looksLikePhone = (v) => (v || '').replace(/[^\d]/g, '').length >= 8;

/**
 * The hero calculator's continuation: the same full-page onboarding screen as `/get-started`, run
 * over THREE steps instead of six.
 *
 * It is a separate page rather than more steps inside the hero card because the card is a sidebar
 * on a marketing page — fine for two sliders, cramped for a form. Continuing hands the whole screen
 * over to the form, which is what the long funnel already does well.
 *
 * Deliberately NOT the six-step funnel: no address, employment, income or debts, so it produces no
 * score. The amounts come through from the calculator the person already set, so they never enter
 * them twice.
 */
export default function QuickStart() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromCalculator = location.state || {};

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    // Empty, not pre-selected. Tracking starts the moment they arrive here, so a seeded default
    // would record "wants a vehicle loan" for someone who left before choosing — and step 1 is
    // supposed to be the question, not a pre-answered one.
    loan_type: '',
    // Prefilled only when a vertical landing page sent one — otherwise the step asks, as before.
    vehicle_category: fromCalculator.vehicle_category || '',
    // Whatever they set on the hero calculator, so the numbers carry across untouched.
    // `??`, not `||`: a deposit of 0 is a real answer, and `||` would quietly rewrite it to 5000 —
    // wrong summary, wrong repayment, wrong figure submitted to the CRM.
    borrow_amount: num(fromCalculator.borrow_amount) ?? 35000,
    deposit_amount: num(fromCalculator.deposit_amount) ?? 5000,
    name: '',
    email: '',
    phone: '',
    consent: false,
    dealer_code: fromCalculator.dealer_code || readDealerCode(),
    // First-touch: which page this visit started on. Read here rather than threaded
    // through navigation state, for the same reason as the dealer code — a scheme where
    // every route into the funnel has to pass it along loses it at the first forgotten link.
    entry_page: readEntryPage(),
  });

  useFormSessionTracking(React, {
    capture: 'hero',
    step,
    stepKey: STEP_KEYS[step],
    totalSteps: TOTAL,
    form: formData,
    // Only when they actually came through the calculator, which is what `location.state` proves.
    // A direct visit, bookmark or reload is a page load like any other and has to earn its way
    // past the gate, or it recreates the false-abandonment problem on a new route.
    engagedFromStart: Boolean(location.state),
  });

  const handleChange = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const loanAmount = formData.borrow_amount - formData.deposit_amount;
  const weekly = weeklyRepayment(loanAmount, DEFAULT_TERM_MONTHS, INDICATIVE_RATE);
  const progress = (step / TOTAL) * 100;

  const canProceed = () => {
    if (step === 1) {
      return Boolean(
        formData.loan_type && (formData.loan_type === 'personal' || formData.vehicle_category),
      );
    }
    if (step === 2) {
      return Boolean(
        formData.name.trim() && looksLikeEmail(formData.email) && looksLikePhone(formData.phone),
      );
    }
    return Boolean(formData.consent);
  };

  async function submit() {
    if (loading) return; // a double-click must not race two leads through
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/applications/quick-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          consent: formData.consent,
          loan_type: formData.loan_type,
          vehicle_category: formData.loan_type === 'personal' ? null : formData.vehicle_category,
          borrow_amount: formData.borrow_amount,
          deposit_amount: formData.deposit_amount,
          dealer_code: formData.dealer_code,
          entry_page: formData.entry_page,
          ...trafficSourceFields(),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Something went wrong. Please try again.');
      }
      const data = await res.json().catch(() => ({}));
      if (data.application_id) trackConverted(data.application_id);
      setDone(true);
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  const next = () => (step === TOTAL ? submit() : setStep((s) => s + 1));
  // Flush before leaving: a client-side navigate fires no `pagehide`, and the tracker is debounced
  // by 1500ms — so typing contact details and heading home immediately lost them from the CRM
  // trail, which is exactly the abandonment worth having.
  const goHome = () => {
    // Locked while the one durable POST is in flight, exactly as the 6-step funnel locks its
    // navigation: leaving mid-submit creates a lead the person believes they abandoned.
    if (loading) return;
    // Only if they actually started. A direct visit that touched nothing is a page load, and
    // flushing it here would open an abandoned-form session full of defaults — the very thing the
    // engagement gate exists to prevent.
    if (!done && (location.state || formData.name || formData.loan_type)) {
      trackStep({
        capture: 'hero',
        step,
        stepKey: STEP_KEYS[step],
        totalSteps: TOTAL,
        form: formData,
        payload: formData,
      });
    }
    navigate('/');
  };
  const back = () => (step === 1 ? goHome() : setStep((s) => s - 1));

  return (
    <div className="min-h-screen bg-black" data-testid="quick-start">
      <div className="p-4 md:p-6">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <button type="button" onClick={goHome} disabled={loading} aria-label="Levr home">
            <img src="/levr-logo.png" alt="Levr" className="h-auto w-28 object-contain md:w-36" />
          </button>
          {!done && (
            <div className="rounded-full bg-[#00D47E] px-4 py-2 text-lg font-bold text-black">
              {Math.round(progress)}%
            </div>
          )}
        </div>
      </div>

      {!done && (
        <div className="px-6 pb-4">
          <div className="mx-auto max-w-2xl">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm text-white/60">Step {step} of {TOTAL}</span>
              <span className="text-sm font-medium text-[#00D47E]">
                {Math.round(progress)}% complete
              </span>
            </div>
            <div className="relative">
              <div className="h-3 overflow-hidden rounded-full bg-gray-800">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-[#00D47E] to-[#00FF94]"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
              <div
                className="absolute top-0 h-3 rounded-full bg-[#00D47E]/30 blur-sm"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-3 flex items-center justify-center gap-1.5">
              {[1, 2, 3].map((s) => (
                <button
                  key={s}
                  onClick={() => s < step && !loading && setStep(s)}
                  disabled={s >= step || loading}
                  aria-label={`Step ${s}`}
                  className={`rounded-full transition-all ${
                    s === step
                      ? 'h-2.5 w-6 bg-[#00D47E]'
                      : s < step
                        ? 'h-2.5 w-2.5 cursor-pointer bg-[#00D47E]/60 hover:bg-[#00D47E]'
                        : 'h-2.5 w-2.5 bg-gray-700'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="px-4 py-6 md:px-6 md:py-8">
        <div className="mx-auto max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={done ? 'done' : step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="rounded-2xl bg-white p-6 shadow-2xl md:p-8"
            >
              {done ? (
                <div className="py-6 text-center" data-testid="quick-success">
                  <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#00D47E]/15">
                    <CheckCircle className="h-8 w-8 text-[#00D47E]" />
                  </div>
                  <h2 className="font-outfit text-2xl font-bold text-black md:text-3xl">
                    We&apos;ve sent you some info and we&apos;ll be in touch!
                  </h2>
                  <p className="mx-auto mt-3 max-w-sm text-gray-600">
                    Check your inbox — a Levr finance specialist will call you to talk through your
                    options.
                  </p>
                  <Button
                    // Plain home: this attempt is submitted and attributed, so carrying the
                    // referral onward would credit the dealer for whatever the next person does.
                    onClick={() => navigate('/')}
                    className="mt-8 rounded-full bg-[#00D47E] px-8 py-6 font-semibold text-black hover:bg-[#00D47E]/90"
                    data-testid="quick-home-btn"
                  >
                    Back to home
                  </Button>
                </div>
              ) : (
                <>
                  {step === 1 && <StepLoanType formData={formData} handleChange={handleChange} />}

                  {/* No relationship or dependents: this flow runs no score, so the questions that
                      exist to feed one would be asked for nothing. */}
                  {step === 2 && (
                    <StepPersonalDetails
                      formData={formData}
                      handleChange={handleChange}
                      showHousehold={false}
                    />
                  )}

                  {step === 3 && (
                    <div className="space-y-6">
                      <div className="mb-8 text-center">
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#00D47E]/10">
                          <ShieldCheck className="h-7 w-7 text-[#00D47E]" />
                        </div>
                        <h2 className="font-outfit mb-2 text-2xl font-bold text-black md:text-3xl">
                          Almost done
                        </h2>
                        <p className="text-gray-600">Check the numbers and we&apos;ll be in touch</p>
                      </div>

                      <div className="rounded-xl bg-gray-50 p-5" data-testid="quick-summary">
                        <Row label="Purchase price" value={formatCurrency(formData.borrow_amount)} />
                        <Row label="Deposit" value={formatCurrency(formData.deposit_amount)} />
                        <Row label="To finance" value={formatCurrency(loanAmount)} strong />
                        <div className="mt-3 border-t border-gray-200 pt-3">
                          <Row
                            label="Estimated weekly"
                            value={formatCurrency(weekly)}
                            strong
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Indicative only, based on {(INDICATIVE_RATE * 100).toFixed(2)}% over{' '}
                            {DEFAULT_TERM_MONTHS} months. Not an offer of finance.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-4">
                        <Checkbox
                          id="quick-consent"
                          checked={formData.consent}
                          onCheckedChange={(c) => handleChange('consent', c === true)}
                          className="mt-0.5"
                          data-testid="quick-consent"
                        />
                        <Label
                          htmlFor="quick-consent"
                          className="cursor-pointer text-sm font-normal leading-relaxed text-gray-600"
                        >
                          I agree to Levr storing these details and a specialist contacting me, per
                          the{' '}
                          <a
                            href="/privacy"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#00D47E] underline underline-offset-2"
                          >
                            Privacy Policy
                          </a>
                          .
                        </Label>
                      </div>

                      {error && (
                        <p className="text-sm text-red-600" data-testid="quick-error">{error}</p>
                      )}
                    </div>
                  )}

                  <div className="mt-8 flex gap-3">
                    <Button
                      variant="outline"
                      onClick={back}
                      disabled={loading}
                      className="flex-1 rounded-full py-6"
                      data-testid="quick-back"
                    >
                      <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
                    </Button>
                    <Button
                      onClick={next}
                      disabled={!canProceed() || loading}
                      data-testid="quick-next"
                      className="flex-1 rounded-full bg-[#00D47E] py-6 font-semibold text-black hover:bg-[#00D47E]/90"
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          {step === TOTAL ? 'Send me the info' : 'Continue'}
                          <ArrowRight className="ml-1.5 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-baseline justify-between py-1">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={strong ? 'font-outfit text-lg font-bold text-black' : 'text-gray-900'}>
        {value}
      </span>
    </div>
  );
}
